import { useEffect, useMemo } from 'react';
import { useMailOrderStore } from '../../../../stores/useMailOrderStore';
import { getIsImpersonating, getSessionUser, type SessionUser } from '../../../../utils/session';
import { WizardStepIndicator } from './steps/WizardStepIndicator';
import { WizardStep1UserSelect } from './steps/WizardStep1UserSelect';
import { WizardStep2HealthProfile } from './steps/WizardStep2HealthProfile';
import { WizardStep3Payment } from './steps/WizardStep3Payment';
import { WizardStep4Demographics } from './steps/WizardStep4Demographics';
import { isAddressComplete, isPhoneComplete } from './steps/WizardStep4Demographics';
import { WizardStep5Review } from './steps/WizardStep5Review';
import { WizardSuccessBanner } from './WizardSuccessBanner';
import { WizardErrorBanner } from './WizardErrorBanner';

const STEP_LABELS = ['Member', 'Health', 'Payment', 'Contact', 'Review'] as const;
const TOTAL_STEPS = 5;

export interface MailOrderWizardProps {
  customerServicePhone: string;
  /** Called after successful enrollment — parent can transition the UI */
  onEnrollmentComplete: () => void;
  /** Called when the member cancels out of the wizard from step 1 */
  onCancel?: () => void;
}

function useIsStepValid(step: number): boolean {
  const { selectedUser, paymentType, eprotectResponse, demographics, acknowledgeTerms } =
    useMailOrderStore();

  return useMemo(() => {
    switch (step) {
      case 1:
        return selectedUser !== null && selectedUser.linkToken !== '';
      case 2:
        return true; // health profile is always valid (None is a valid selection)
      case 3:
        // Payment step manages its own advance — never block from wizard nav POV
        return true;
      case 4: {
        const { phone, address } = demographics;
        return (
          isPhoneComplete(phone.areaCode, phone.phoneNumber) &&
          isAddressComplete(address.street1, address.city, address.state, address.zipCode)
        );
      }
      case 5:
        return (
          acknowledgeTerms && (paymentType === 'check' || !!eprotectResponse?.paypageRegistrationId)
        );
      default:
        return true;
    }
  }, [step, selectedUser, paymentType, eprotectResponse, demographics, acknowledgeTerms]);
}

export function MailOrderWizard({
  customerServicePhone,
  onEnrollmentComplete,
  onCancel,
}: MailOrderWizardProps) {
  const {
    currentStep,
    nextStep,
    prevStep,
    submitEnrollment,
    submitSuccess,
    submitError,
    isSubmitting,
    selectedUser,
    resetWizard,
  } = useMailOrderStore();

  // Reset all wizard state on unmount so stale PHI/card data from a prior
  // member never leaks if the wizard is re-opened in the same SPA session.
  useEffect(() => {
    return () => { resetWizard(); };
  }, [resetWizard]);

  const isStepValid = useIsStepValid(currentStep);
  const isPaymentStep = currentStep === 3;
  const isReviewStep = currentStep === TOTAL_STEPS;
  const isImpersonating = getIsImpersonating();

  // When enrollment succeeds and IsInGuardian is now true, the parent
  // RefillsPage will re-render the standard refills experience automatically
  // because it reads IsInGuardian from the updated sessionStorage user.
  // We call onEnrollmentComplete to signal the parent to reload.
  useEffect(() => {
    if (submitSuccess) {
      const user = getSessionUser<SessionUser>();
      if (user?.IsInGuardian) {
        onEnrollmentComplete();
      }
    }
  }, [submitSuccess, onEnrollmentComplete]);

  if (submitSuccess) {
    return (
      <WizardSuccessBanner
        memberName={selectedUser?.displayName ?? ''}
        onReturnHome={onEnrollmentComplete}
      />
    );
  }

  return (
    <div className="mail-order-wizard">
      <WizardStepIndicator
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        stepLabels={STEP_LABELS}
      />

      <div className="wizard-step-content mb-4">
        {currentStep === 1 && <WizardStep1UserSelect />}
        {currentStep === 2 && <WizardStep2HealthProfile />}
        {currentStep === 3 && (
          <WizardStep3Payment
            customerServicePhone={customerServicePhone}
            onAdvance={nextStep}
            onBack={prevStep}
          />
        )}
        {currentStep === 4 && (
          <WizardStep4Demographics customerServicePhone={customerServicePhone} />
        )}
        {currentStep === 5 && <WizardStep5Review customerServicePhone={customerServicePhone} />}
      </div>

      {/* Error banner (surfaces submission errors from step 5) */}
      {submitError && (
        <WizardErrorBanner
          message={submitError}
          customerServicePhone={customerServicePhone}
          onRetry={() => useMailOrderStore.setState({ submitError: null })}
        />
      )}

      {/* Generic navigation — step 3 renders its own buttons */}
      {!isPaymentStep && (
        <div className="d-flex gap-2" style={{ paddingTop: '1rem' }}>
          {currentStep > 1 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={prevStep}
              disabled={isSubmitting}
            >
              Back
            </button>
          )}

          {currentStep === 1 && (
            <button
              type="button"
              className="btn btn-default"
              onClick={() => onCancel?.()}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}

          {!isReviewStep && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={nextStep}
              disabled={!isStepValid || isSubmitting}
              style={{ float: 'right' }}
            >
              Continue
            </button>
          )}

          {isReviewStep && (
            <button
              type="button"
              className="btn btn-success"
              onClick={submitEnrollment}
              disabled={!isStepValid || isSubmitting || isImpersonating}
              style={{ float: 'right' }}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Submitting…
                </>
              ) : (
                'Submit Enrollment'
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
